import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { ContactFormErrors, ContactFormValues } from '@/constants/contact-form'

import Link from 'next/link'
import { useEffect, useId, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { CircleAlert, ArrowUpRight, Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EASE_EXPO } from '@/components/motion/reveal'
import { contact } from '@/constants/site'

import {
    FIELD_LIMITS,
    SALUTATIONS,
    TOPICS,
    validateContact
} from '@/constants/contact-form'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const INITIAL_VALUES: ContactFormValues = {
    salutation: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    topic: '',
    message: '',
    consent: false
}

const FIELD_ORDER: Array<keyof ContactFormValues> = ['salutation', 'firstName', 'lastName', 'company', 'phone', 'email', 'topic', 'message', 'consent']

interface FieldShellProps {
    id: string
    label: string
    optional?: boolean
    error?: string
    className?: string
    children: ReactNode
}

const FieldShell: FC<FieldShellProps> = ({ id, label, optional, error, className, children }): ReactNode => {
    return (
        <div className={cn('group/field relative', className)}>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 font-mono text-[0.68rem] uppercase tracking-[0.18em] text-ink/65'>
                <span>
                    {label}
                    {!optional && <span aria-hidden='true' className='text-brand-ink'> *</span>}
                </span>
                {optional && <span className='normal-case tracking-normal text-ink/45'>optional</span>}
            </label>

            {children}

            <AnimatePresence initial={false}>
                {error && (
                    <motion.p
                        id={`${id}-fehler`}
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className='mt-2 flex items-center gap-1.5 text-sm text-destructive'
                    >
                        <CircleAlert aria-hidden='true' className='size-3.5 shrink-0' strokeWidth={2} />
                        {error}
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    )
}

const inputClasses = (hasError: boolean): string => cn(
    'mt-2 block w-full rounded-none border-0 border-b bg-transparent px-0 py-3 text-[1.05rem] text-ink placeholder:text-ink/35',
    'transition-colors duration-300 focus:outline-none focus-visible:outline-none',
    hasError ? 'border-destructive focus:border-destructive' : 'border-ink/20 hover:border-ink/45 focus:border-ink'
)

export const ContactForm: FC = (): ReactNode => {
    const formId = useId()
    const [values, setValues] = useState<ContactFormValues>(INITIAL_VALUES)
    const [errors, setErrors] = useState<ContactFormErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [website, setWebsite] = useState('')
    const startedAt = useRef(0)
    const formRef = useRef<HTMLFormElement>(null)
    const successRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        startedAt.current = Date.now()
    }, [])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const fieldId = (name: keyof ContactFormValues): string => `${formId}-${name}`

    const update = (name: keyof ContactFormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const value = event.target instanceof HTMLInputElement && event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value

        setValues(current => ({ ...current, [name]: value }))

        if (errors[name]) {
            setErrors(current => {
                const next = { ...current }
                delete next[name]
                return next
            })
        }
    }

    const focusFirstError = (found: ContactFormErrors): void => {
        const first = FIELD_ORDER.find(name => found[name])

        if (!first) return

        const element = formRef.current?.querySelector<HTMLElement>(`#${CSS.escape(fieldId(first))}`)

        element?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const found = validateContact(values)

        setErrors(found)

        if (Object.keys(found).length > 0) {
            focusFirstError(found)
            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/kontakt', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    website,
                    startedAt: startedAt.current,
                    page: window.location.pathname
                })
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, message?: string, errors?: ContactFormErrors } | null

            if (response.ok && result?.ok) {
                setStatus('success')
                return
            }

            if (result?.errors && Object.keys(result.errors).length > 0) {
                setErrors(result.errors)
                setStatus('idle')
                focusFirstError(result.errors)
                return
            }

            setServerMessage(result?.message ?? 'Ihre Nachricht konnte gerade nicht gesendet werden.')
            setStatus('error')
        }
        catch {
            setServerMessage('Es besteht gerade keine Verbindung. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.')
            setStatus('error')
        }
    }

    const reset = (): void => {
        setValues(INITIAL_VALUES)
        setErrors({})
        setStatus('idle')
        setServerMessage('')
        startedAt.current = Date.now()
    }

    const describedBy = (name: keyof ContactFormValues): string | undefined => errors[name] ? `${fieldId(name)}-fehler` : undefined

    return (
        <div className='relative'>
            <AnimatePresence mode='wait' initial={false}>
                {status === 'success'
                    ? (
                        <motion.div
                            key='erfolg'
                            ref={successRef}
                            tabIndex={-1}
                            role='status'
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.8, ease: EASE_EXPO }}
                            className='flex min-h-[32rem] flex-col justify-between gap-12 rounded-[1.75rem] bg-graphite p-8 text-paper outline-none md:p-12'
                        >
                            <span className='grid size-16 place-items-center rounded-full bg-brand text-ink'>
                                <Check aria-hidden='true' className='size-7' strokeWidth={2} />
                            </span>
                            <div>
                                <p className='font-heading text-[clamp(2.4rem,4.4vw,4rem)] leading-[0.98]'>
                                    Vielen Dank, {values.firstName}.
                                </p>
                                <p className='mt-6 max-w-md text-lg leading-relaxed text-paper/70'>
                                    Ihre Nachricht ist angekommen. Ich melde mich zeitnah persönlich bei Ihnen. Wenn es eilt, erreichen Sie mich telefonisch unter
                                    {' '}
                                    <a href={contact.phone.href} className='tabular text-paper underline decoration-brand underline-offset-4'>
                                        {contact.phone.display}
                                    </a>
                                    .
                                </p>
                            </div>
                            <button
                                type='button'
                                onClick={reset}
                                className='link-swap self-start pb-0.5 text-sm font-medium text-paper'
                            >
                                Weitere Nachricht schreiben
                            </button>
                        </motion.div>
                    )
                    : (
                        <motion.form
                            key='formular'
                            ref={formRef}
                            noValidate
                            onSubmit={handleSubmit}
                            aria-describedby={`${formId}-hinweis`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.5, ease: EASE_EXPO }}
                            className='rounded-[1.75rem] bg-card p-6 shadow-[0_40px_90px_-60px_rgba(20,21,23,0.45)] ring-1 ring-ink/8 sm:p-10 lg:p-12'
                        >
                            <p id={`${formId}-hinweis`} className='text-sm text-ink/60'>
                                Felder mit
                                {' '}
                                <span className='text-brand-ink'>*</span>
                                {' '}
                                sind Pflichtfelder.
                            </p>

                            <div className='mt-8 grid gap-x-8 gap-y-8 sm:grid-cols-2'>
                                <FieldShell id={fieldId('salutation')} label='Anrede' optional error={errors.salutation} className='sm:col-span-2 sm:max-w-[16rem]'>
                                    <select
                                        id={fieldId('salutation')}
                                        name='salutation'
                                        value={values.salutation}
                                        onChange={update('salutation')}
                                        aria-invalid={Boolean(errors.salutation)}
                                        aria-describedby={describedBy('salutation')}
                                        className={cn(inputClasses(Boolean(errors.salutation)), 'select-chevron')}
                                    >
                                        <option value=''>Bitte wählen</option>
                                        {SALUTATIONS.map(option => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </FieldShell>

                                <FieldShell id={fieldId('firstName')} label='Vorname' error={errors.firstName}>
                                    <input
                                        id={fieldId('firstName')}
                                        name='firstName'
                                        type='text'
                                        autoComplete='given-name'
                                        maxLength={FIELD_LIMITS.name}
                                        value={values.firstName}
                                        onChange={update('firstName')}
                                        aria-invalid={Boolean(errors.firstName)}
                                        aria-describedby={describedBy('firstName')}
                                        aria-required='true'
                                        className={inputClasses(Boolean(errors.firstName))}
                                    />
                                </FieldShell>

                                <FieldShell id={fieldId('lastName')} label='Nachname' error={errors.lastName}>
                                    <input
                                        id={fieldId('lastName')}
                                        name='lastName'
                                        type='text'
                                        autoComplete='family-name'
                                        maxLength={FIELD_LIMITS.name}
                                        value={values.lastName}
                                        onChange={update('lastName')}
                                        aria-invalid={Boolean(errors.lastName)}
                                        aria-describedby={describedBy('lastName')}
                                        aria-required='true'
                                        className={inputClasses(Boolean(errors.lastName))}
                                    />
                                </FieldShell>

                                <FieldShell id={fieldId('email')} label='E-Mail' error={errors.email}>
                                    <input
                                        id={fieldId('email')}
                                        name='email'
                                        type='email'
                                        inputMode='email'
                                        autoComplete='email'
                                        maxLength={FIELD_LIMITS.email}
                                        value={values.email}
                                        onChange={update('email')}
                                        aria-invalid={Boolean(errors.email)}
                                        aria-describedby={describedBy('email')}
                                        aria-required='true'
                                        className={inputClasses(Boolean(errors.email))}
                                    />
                                </FieldShell>

                                <FieldShell id={fieldId('phone')} label='Telefon' optional error={errors.phone}>
                                    <input
                                        id={fieldId('phone')}
                                        name='phone'
                                        type='tel'
                                        inputMode='tel'
                                        autoComplete='tel'
                                        maxLength={FIELD_LIMITS.phone}
                                        value={values.phone}
                                        onChange={update('phone')}
                                        aria-invalid={Boolean(errors.phone)}
                                        aria-describedby={describedBy('phone')}
                                        className={inputClasses(Boolean(errors.phone))}
                                    />
                                </FieldShell>

                                <FieldShell id={fieldId('company')} label='Firma' optional error={errors.company}>
                                    <input
                                        id={fieldId('company')}
                                        name='company'
                                        type='text'
                                        autoComplete='organization'
                                        maxLength={FIELD_LIMITS.company}
                                        value={values.company}
                                        onChange={update('company')}
                                        aria-invalid={Boolean(errors.company)}
                                        aria-describedby={describedBy('company')}
                                        className={inputClasses(Boolean(errors.company))}
                                    />
                                </FieldShell>

                                <FieldShell id={fieldId('topic')} label='Anliegen' optional error={errors.topic}>
                                    <select
                                        id={fieldId('topic')}
                                        name='topic'
                                        value={values.topic}
                                        onChange={update('topic')}
                                        aria-invalid={Boolean(errors.topic)}
                                        aria-describedby={describedBy('topic')}
                                        className={cn(inputClasses(Boolean(errors.topic)), 'select-chevron')}
                                    >
                                        <option value=''>Bitte wählen</option>
                                        {TOPICS.map(option => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </FieldShell>

                                <FieldShell id={fieldId('message')} label='Ihre Nachricht' error={errors.message} className='sm:col-span-2'>
                                    <textarea
                                        id={fieldId('message')}
                                        name='message'
                                        rows={5}
                                        maxLength={FIELD_LIMITS.message}
                                        value={values.message}
                                        onChange={update('message')}
                                        aria-invalid={Boolean(errors.message)}
                                        aria-describedby={describedBy('message')}
                                        aria-required='true'
                                        placeholder='Worum geht es? Je konkreter, desto besser kann ich mich vorbereiten.'
                                        className={cn(inputClasses(Boolean(errors.message)), 'min-h-[9rem] resize-y leading-relaxed')}
                                        data-lenis-prevent
                                    />
                                </FieldShell>

                                <div aria-hidden='true' className='absolute -left-[9999px] top-auto h-px w-px overflow-hidden'>
                                    <label htmlFor={`${formId}-website`}>
                                        Website
                                    </label>
                                    <input
                                        id={`${formId}-website`}
                                        name='website'
                                        type='text'
                                        tabIndex={-1}
                                        autoComplete='off'
                                        value={website}
                                        onChange={event => setWebsite(event.target.value)}
                                    />
                                </div>

                                <div className='sm:col-span-2'>
                                    <label htmlFor={fieldId('consent')} className='flex cursor-pointer items-start gap-4'>
                                        <span className='relative mt-0.5 grid size-6 shrink-0 place-items-center'>
                                            <input
                                                id={fieldId('consent')}
                                                name='consent'
                                                type='checkbox'
                                                checked={values.consent}
                                                onChange={update('consent')}
                                                aria-invalid={Boolean(errors.consent)}
                                                aria-describedby={describedBy('consent')}
                                                aria-required='true'
                                                className={cn(
                                                    'peer size-6 cursor-pointer appearance-none rounded-md border bg-transparent transition-colors duration-300 checked:border-ink checked:bg-ink',
                                                    errors.consent ? 'border-destructive' : 'border-ink/30 hover:border-ink/60'
                                                )}
                                            />
                                            <Check aria-hidden='true' className='pointer-events-none absolute size-4 text-paper opacity-0 transition-opacity peer-checked:opacity-100' strokeWidth={2.5} />
                                        </span>
                                        <span className='text-[0.95rem] leading-relaxed text-ink/75'>
                                            Ich bin damit einverstanden, dass meine Angaben zur Beantwortung meiner Anfrage verarbeitet werden. Weitere Informationen finden Sie in der
                                            {' '}
                                            <Link href='/datenschutz' className='text-ink underline decoration-brand underline-offset-4'>
                                                Datenschutzerklärung
                                            </Link>
                                            .
                                            <span aria-hidden='true' className='text-brand-ink'> *</span>
                                        </span>
                                    </label>

                                    <AnimatePresence initial={false}>
                                        {errors.consent && (
                                            <motion.p
                                                id={`${fieldId('consent')}-fehler`}
                                                initial={{ opacity: 0, y: -4 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                className='ml-10 mt-2 flex items-center gap-1.5 text-sm text-destructive'
                                            >
                                                <CircleAlert aria-hidden='true' className='size-3.5 shrink-0' strokeWidth={2} />
                                                {errors.consent}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>

                            <AnimatePresence initial={false}>
                                {status === 'error' && (
                                    <motion.div
                                        role='alert'
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.4, ease: EASE_EXPO }}
                                        className='overflow-hidden'
                                    >
                                        <div className='mt-8 flex gap-3 rounded-2xl bg-destructive/8 p-5 text-[0.95rem] leading-relaxed text-ink'>
                                            <CircleAlert aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-destructive' strokeWidth={1.75} />
                                            <p>
                                                {serverMessage}
                                                {' '}
                                                Telefon:
                                                {' '}
                                                <a href={contact.phone.href} className='tabular font-medium underline decoration-brand underline-offset-4'>
                                                    {contact.phone.display}
                                                </a>
                                                {' '}
                                                oder E-Mail:
                                                {' '}
                                                <a href={`mailto:${contact.email}`} className='font-medium underline decoration-brand underline-offset-4'>
                                                    {contact.email}
                                                </a>
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className='mt-10 flex flex-col-reverse gap-5 sm:flex-row sm:items-center sm:justify-between'>
                                <p className='text-sm text-ink/55'>
                                    Ihre Angaben werden vertraulich behandelt.
                                </p>

                                <button
                                    type='submit'
                                    disabled={status === 'submitting'}
                                    className='group/button relative isolate inline-flex h-13 items-center justify-between gap-3 overflow-hidden rounded-full bg-ink pl-6 pr-1.5 text-[0.95rem] font-medium text-paper transition-colors duration-500 ease-expo hover:text-ink disabled:cursor-wait disabled:opacity-80'
                                >
                                    <span aria-hidden='true' className='absolute inset-0 -z-10 origin-bottom scale-y-0 rounded-full bg-brand transition-transform duration-500 ease-expo group-hover/button:scale-y-100' />
                                    <span>
                                        {status === 'submitting' ? 'Wird gesendet …' : 'Nachricht senden'}
                                    </span>
                                    <span aria-hidden='true' className='grid size-10 place-items-center rounded-full bg-paper/10 transition-colors duration-500 group-hover/button:bg-ink group-hover/button:text-paper'>
                                        {status === 'submitting'
                                            ? <LoaderCircle className='size-4 animate-spin' strokeWidth={1.75} />
                                            : <ArrowUpRight className='size-4 transition-transform duration-500 ease-expo group-hover/button:rotate-45' strokeWidth={1.75} />}
                                    </span>
                                </button>
                            </div>
                        </motion.form>
                    )}
            </AnimatePresence>
        </div>
    )
}
